/** Reviewed SCOM, Chroma 2233-B and CA-410 RS232 protocols, with no discovery. */
import { HardwareError, ProtocolError, UnsupportedCapability, WebSerialTransport, SERIAL_OPTIONS } from './webSerialTransport';

const bytes = text => {
  if (typeof text !== 'string' || text.length > 256 || [...text].some(character => character.charCodeAt(0) > 127)) throw new ProtocolError('Instrument command must be bounded ASCII.');
  return Uint8Array.from(text, character => character.charCodeAt(0));
};
const text = value => String.fromCharCode(...value);
const checksum = values => (255 - (values.reduce((total, value) => total + value, 0) % 256) + 256) % 256;
const integer = (value, low, high) => typeof value === 'number' && Number.isInteger(value) && value >= low && value <= high;
const plain = value => value && typeof value === 'object' && !Array.isArray(value) && [Object.prototype, null].includes(Object.getPrototypeOf(value));
const keysMatch = (value, keys) => plain(value) && Object.keys(value).length === keys.length && Object.keys(value).every(key => keys.includes(key));

export function buildScomFrame(command, data = new Uint8Array(), { address = 255, attention = 7 } = {}) {
  if (typeof command !== 'string' || !/^[A-Z]{3}$/.test(command) || !(data instanceof Uint8Array) || data.length > 255
    || !integer(address, 0, 255) || ![6, 7, 21].includes(attention)) throw new ProtocolError('SCOM frame fields are invalid.');
  const header = Uint8Array.from([attention, address, ...bytes(command), data.length]);
  return Uint8Array.from([...header, checksum(header), ...data, ...(data.length ? [checksum(data)] : [])]);
}

export function parseScomFrame(frame, { expectedCommand, expectedAddress = 255 } = {}) {
  if (!(frame instanceof Uint8Array) || frame.length < 7) throw new ProtocolError('SCOM reply is truncated.');
  const count = frame[5];
  if (frame.length !== 7 + count + (count ? 1 : 0)) throw new ProtocolError('SCOM reply length does not match its header.');
  const total = values => values.reduce((sum, value) => sum + value, 0) % 256;
  if (total(frame.slice(0, 7)) !== 255 || (count && total(frame.slice(7)) !== 255)) throw new ProtocolError('SCOM reply checksum is invalid.');
  if (frame[0] !== 6) throw new ProtocolError('SCOM request was not acknowledged.');
  if (frame[1] !== expectedAddress) throw new ProtocolError('SCOM reply address does not match the request.');
  const command = text(frame.slice(2, 5));
  if (!/^[A-Z]{3}$/.test(command) || (expectedCommand && command !== expectedCommand)) throw new ProtocolError('SCOM reply command does not match the request.');
  return { command, address: frame[1], data: frame.slice(7, 7 + count) };
}

export function encodeServiceTag(serviceTag) {
  if (typeof serviceTag !== 'string' || !/^[A-I][0-9]{9}$/.test(serviceTag)) throw new HardwareError('Service tag must be A-I followed by exactly nine digits.');
  const value = Number(`${serviceTag.charCodeAt(0) - 64}${serviceTag.slice(1)}`);
  if (value > 0xFFFFFFFF) throw new HardwareError('Service tag exceeds the monitor 32-bit serial capacity.');
  return value;
}

class Instrument {
  constructor(transport, format) { this.transport = transport; this.format = format; }
  async connect() {
    if (this.transport.settings && ['baudRate', 'dataBits', 'stopBits', 'parity', 'flowControl'].some(key => this.transport.settings[key] !== this.format[key])) {
      throw new HardwareError('Serial settings do not match this instrument reviewed interface.');
    }
    await this.transport.connect(); return this;
  }
  async close() { await this.transport.close(); }
  reject(error) { this.transport.fail?.(error); throw error; }
}

export class ScomClient extends Instrument {
  constructor(transport) { super(transport, SERIAL_OPTIONS.monitor); }
  async request(command, data) {
    try {
      const reply = await this.transport.exchange(buildScomFrame(command, data), { scom: true, maxBytes: 264 });
      return parseScomFrame(reply, { expectedCommand: command }).data;
    } catch (error) { return this.reject(error); }
  }
  async identify() {
    if (text(await this.request('MAN')) !== 'NIN') this.reject(new ProtocolError('SCOM manufacturer identity does not match NIN.'));
    return { manufacturer: 'NIN', interface: 'RS232', address: 255 };
  }
  async serviceTag() {
    const data = await this.request('SNB');
    if (data.length !== 4) this.reject(new ProtocolError('SCOM serial must contain exactly four bytes.'));
    return new DataView(data.buffer, data.byteOffset, data.byteLength).getUint32(0, false);
  }
  async setBacklight(percent) {
    if (!integer(percent, 0, 100)) throw new HardwareError('Backlight percent must be an integer from 0 to 100.');
    const wire = Math.floor((percent * 255 + 50) / 100), observed = await this.request('BRT', Uint8Array.of(wire));
    if (observed.length !== 1 || observed[0] !== wire) this.reject(new ProtocolError('SCOM backlight acknowledgement differs from the requested level.'));
    return percent;
  }
  async readEdid() { throw new UnsupportedCapability('SCOM has no qualified EDID readback command; use independently captured EDID bytes.'); }
}

function asciiReply(data, terminator) {
  if (!(data instanceof Uint8Array) || data.length < 1 || data[data.length - 1] !== terminator) throw new ProtocolError('Instrument reply is incomplete.');
  const reply = text(data.slice(0, -1)).trim();
  if (!reply || [...reply].some(character => character.charCodeAt(0) < 32 || character.charCodeAt(0) > 126)) throw new ProtocolError('Instrument reply contains invalid ASCII or control characters.');
  return reply;
}

export class ChromaClient extends Instrument {
  constructor(transport) { super(transport, SERIAL_OPTIONS.generator); }
  async command(command, report = false) {
    try {
      const answer = asciiReply(await this.transport.exchange(bytes(command), { terminator: 59 }), 59);
      if (answer.toUpperCase().startsWith('NG')) this.reject(new ProtocolError(`Chroma rejected the command: ${answer}.`));
      if (!report && answer.toUpperCase() !== 'OK') this.reject(new ProtocolError('Chroma did not return an OK acknowledgement.'));
      return answer;
    } catch (error) { return this.reject(error); }
  }
  async identify() {
    await this.command('enable;');
    const model = await this.command('REPORT MODEL;', true);
    if (!/(^|[^0-9])2233[- ]?B(?![A-Z0-9])/.test(model.toUpperCase())) this.reject(new UnsupportedCapability('Pattern generator identity does not identify Chroma 2233-B.'));
    return { model, firmware: await this.command('REPORT VER;', true) };
  }
  async setTiming(value) {
    if (!integer(value, 0, 4000)) throw new HardwareError('Timing number must be an explicit integer from 0 to 4000.');
    for (let index = 0; index < 3; index += 1) await this.command(`run tim ${String(value).padStart(4, '0')};`);
  }
  async setRgb(red, green, blue) {
    const values = [red, green, blue];
    if (!values.every(value => integer(value, 0, 255))) throw new HardwareError('Pattern RGB values must be integers from 0 to 255.');
    const encoded = values.map(value => Math.floor((value * 4095 + 127) / 255));
    await this.command('run ptn 0011;');
    await this.command(`RUN PEN 0 R ${encoded[0]} G ${encoded[1]} B ${encoded[2]};`);
  }
  async readEdid() { throw new UnsupportedCapability('Chroma READ EDID reply framing is not qualified; capture EDID independently.'); }
}

const SYNC = Object.freeze({ NTSC: 0, PAL: 1, UNIVERSAL: 3, INTERNAL: 4, MANUAL: 5 });
const SPEED = Object.freeze({ SLOW: 0, FAST: 1, 'LTD.AUTO': 2, AUTO: 3 });
function exactDecimal(source) {
  if (typeof source !== 'string' || source.length > 100) return null;
  const match = /^([+-]?)(?:(\d+)(?:\.(\d*))?|\.(\d+))(?:[eE]([+-]?\d+))?$/.exec(source);
  if (!match) return null;
  const fraction = match[3] ?? match[4] ?? '', exponent = Number(match[5] || 0);
  if (!Number.isSafeInteger(exponent) || Math.abs(exponent) > 1000) return null;
  let digits = `${match[2] || ''}${fraction}`.replace(/^0+/, ''), power = exponent - fraction.length;
  if (!digits) return { key: '0', power: 0 };
  while (digits.endsWith('0')) { digits = digits.slice(0, -1); power += 1; }
  return { key: `${match[1] === '-' ? '-' : ''}${digits}e${power}`, power };
}
function decimalSetting(value, name, low, high, places) {
  const source = typeof value === 'string' ? value.trim() : value;
  const exact = ['number', 'string'].includes(typeof source) ? exactDecimal(String(source)) : null;
  if (!exact || exact.power < -places) {
    throw new HardwareError(`${name} is outside the documented range or precision.`);
  }
  const number = Number(source);
  if (!Number.isFinite(number) || number < low || number > high || Number(number.toFixed(places)) !== number) throw new HardwareError(`${name} is outside the documented range or precision.`);
  return number.toFixed(places);
}

export function validateMeterSettings(settings) {
  if (!plain(settings)) throw new HardwareError('Explicit meter settings are required.');
  const { syncMode, speed, memoryChannel } = settings;
  if (!Object.prototype.hasOwnProperty.call(SYNC, syncMode) || !Object.prototype.hasOwnProperty.call(SPEED, speed)) throw new UnsupportedCapability('Meter sync mode or speed is not qualified by this driver.');
  if (!integer(memoryChannel, 0, 99)) throw new HardwareError('An explicit meter memory channel from 0 to 99 is required.');
  const required = ['syncMode', 'speed', 'memoryChannel'];
  let value = null;
  if (syncMode === 'MANUAL') { required.push('integrationMs'); value = decimalSetting(settings.integrationMs, 'Manual integration', 4, 4000, 1); }
  if (syncMode === 'INTERNAL') { required.push('frequencyHz'); value = decimalSetting(settings.frequencyHz, 'Internal frequency', 0.5, 240, 2); }
  if (!keysMatch(settings, required)) throw new UnsupportedCapability('Meter settings contain missing or unsupported keys.');
  return { syncMode, speed, memoryChannel, value };
}

export class Ca410Client extends Instrument {
  constructor(transport, { expectedProbe = 'CA-P427', operationTimeoutMs = 35000 } = {}) {
    super(transport, SERIAL_OPTIONS.meter);
    if (typeof expectedProbe !== 'string' || !/^CA-P[0-9]{3}$/.test(expectedProbe) || typeof operationTimeoutMs !== 'number' || !Number.isFinite(operationTimeoutMs) || operationTimeoutMs < 50 || operationTimeoutMs > 120000) throw new HardwareError('Invalid explicit CA-410 probe or operation timeout.');
    this.expectedProbe = expectedProbe; this.operationTimeoutMs = operationTimeoutMs;
    this.identified = false; this.configured = false; this.zeroed = false; this.processor = false; this.settingsReadback = null;
  }
  async connect() { this.identified = false; this.configured = false; this.zeroed = false; this.settingsReadback = null; return super.connect(); }
  async close() { this.identified = false; this.configured = false; this.zeroed = false; this.settingsReadback = null; await super.close(); }
  async command(command) {
    try {
      const answer = asciiReply(await this.transport.exchange(bytes(`${command}\r`), { terminator: 13, timeoutMs: this.operationTimeoutMs }), 13);
      const fields = answer.split(',').map(field => field.trim());
      if (fields[0] !== 'OK00') this.reject(new ProtocolError('CA-410 did not report normal completion.'));
      return fields;
    } catch (error) { return this.reject(error); }
  }
  async ack(command) { if ((await this.command(command)).length !== 1) this.reject(new ProtocolError('CA-410 acknowledgement has unexpected fields.')); }
  async identity(probe) {
    const fields = await this.command(`IDO,${probe},1`);
    if (fields.length !== 7 || fields[1] !== 'CA-410' || !/^[0-9]{5}$/.test(fields[2]) || !/^Ver\.[0-9]+\.[0-9]+\.[0-9]+$/.test(fields[4]) || !/^[0-9]{8}$/.test(fields[5])) this.reject(new ProtocolError('CA-410 extended identity format is invalid.'));
    return { product: fields[1], variation: fields[2], model: fields[3], firmware: fields[4], serial: fields[5], custom: fields[6] };
  }
  async identify() {
    this.identified = false;
    await this.ack('COM,1');
    const device = await this.identity(0); this.processor = device.model === 'CA-DP40';
    if (!this.processor && device.model !== this.expectedProbe) this.reject(new UnsupportedCapability('CA-410 connection is not the configured probe or CA-DP40 processor.'));
    const probe = this.processor ? await this.identity(1) : device;
    if (probe.model !== this.expectedProbe) this.reject(new UnsupportedCapability('CA-410 probe identity does not match the station configuration.'));
    this.identified = true; return { device, probe, connection: this.processor ? 'processor' : 'probe' };
  }
  async str(selector, probeScoped = false) {
    const fields = await this.command(probeScoped && this.processor ? `STR,1,${selector}` : `STR,${selector}`);
    if (fields.length !== 2) this.reject(new ProtocolError('CA-410 settings readback has unexpected fields.'));
    return fields[1];
  }
  async configure(settings) {
    this.configured = false; this.zeroed = false; this.settingsReadback = null;
    const { syncMode, speed, memoryChannel, value } = validateMeterSettings(settings);
    if (!this.identified) await this.identify();
    const commands = ['OPR,1', 'DPR,1', 'MDS,0', 'TMS,0', 'MMS,1', `SCS,${SYNC[syncMode]}${value !== null ? `,${value}` : ''}`, `FSC,${SPEED[speed]}`, `MCH,1,${memoryChannel}`];
    for (const command of commands) await this.ack(command);
    const expected = [[0, false, 0], [1, false, SYNC[syncMode]], [7, false, SPEED[speed]], [18, false, 0], [3, true, memoryChannel], [26, true, 1]];
    if (this.processor) expected.push([11, false, 1], [12, false, 1]);
    const raw = {};
    for (const [selector, scoped, wanted] of expected) {
      const observed = await this.str(selector, scoped);
      if (!/^[0-9]+$/.test(observed) || Number(observed) !== wanted) this.reject(new ProtocolError(`CA-410 STR ${selector} readback does not match the chosen setting.`));
      raw[selector] = observed;
    }
    if (value !== null) {
      const selector = syncMode === 'MANUAL' ? 17 : 28, observed = await this.str(selector);
      if (!exactDecimal(observed) || exactDecimal(observed).key !== exactDecimal(value).key) this.reject(new ProtocolError('CA-410 integration/frequency readback does not match.'));
      raw[selector] = observed;
    }
    this.settingsReadback = { ...settings, mode: 'xyY', probe: 1, colorOnly: true, trigger: false, raw };
    this.configured = true; return this.settingsReadback;
  }
  async zero() {
    this.zeroed = false;
    if (!this.configured) throw new HardwareError('Configure and read back the meter conditions before zero calibration.');
    await this.ack('ZRC');
    const state = await this.str(23, true);
    if (state !== '2') this.reject(new ProtocolError('CA-410 zero calibration is not reported complete.'));
    this.zeroed = true; return { reply: 'OK00', state, internalShutter: true };
  }
  async measureXyY() {
    if (!this.configured || !this.zeroed) throw new HardwareError('A checked meter configuration and completed zero calibration are required.');
    const fields = await this.command('MES,1');
    if (fields.length !== 8 || fields[1] !== 'P1' || fields[2] !== '0') this.reject(new ProtocolError('CA-410 measurement must be a complete P1 xyY extended-format reply.'));
    const numeric = fields.slice(3);
    if (numeric.some(value => !/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(value))) this.reject(new ProtocolError('CA-410 measurement contains non-numeric fields.'));
    const values = numeric.map(Number);
    if (!values.every(Number.isFinite)) this.reject(new ProtocolError('CA-410 measurement contains non-finite values.'));
    const [x, y, Y] = values;
    if (x < 0 || x > 1 || y < 0 || y > 1 || x + y > 1.00001 || Y < 0) this.reject(new ProtocolError('CA-410 xyY measurement is outside the physical range.'));
    return [x, y, Y];
  }
}

export function createNorthstarBench({ meterPort, generatorPort, monitorPort }, { signal, expectedProbe = 'CA-P427',
  operationTimeoutMs = 35000, timeoutMs = 3000, openTimeoutMs = 10000, closeTimeoutMs = 1000, clock,
  transportFactory = (port, settings, options) => new WebSerialTransport(port, settings, options) } = {}) {
  const ports = [meterPort, generatorPort, monitorPort];
  if (ports.some(port => !port || typeof port !== 'object') || new Set(ports).size !== 3) throw new HardwareError('The meter, generator and monitor require three distinct selected ports.');
  const options = { signal, timeoutMs, openTimeoutMs, closeTimeoutMs, ...(clock ? { clock } : {}) };
  const meter = new Ca410Client(transportFactory(meterPort, SERIAL_OPTIONS.meter, options), { expectedProbe, operationTimeoutMs });
  const generator = new ChromaClient(transportFactory(generatorPort, SERIAL_OPTIONS.generator, options));
  const monitor = new ScomClient(transportFactory(monitorPort, SERIAL_OPTIONS.monitor, options));
  const clients = [meter, generator, monitor];
  const bench = {
    mode: 'hardware', observedInitialSerial: null, cleanupErrors: [],
    async prepare(profile) {
      if (!profile?.station || typeof profile.station.meterSettings !== 'string' || profile.station.meterSettings.length > 500 || !integer(profile.timingNumber, 0, 4000)) throw new HardwareError('Explicit bounded meter settings and timing number are required.');
      let settings;
      try { settings = JSON.parse(profile.station.meterSettings); } catch { throw new HardwareError('Meter settings must be valid JSON.'); }
      validateMeterSettings(settings);
      try {
        for (const client of clients) await client.connect();
        const meterId = await meter.identify(), generatorId = await generator.identify(), monitorId = await monitor.identify();
        bench.observedInitialSerial = await monitor.serviceTag();
        const readback = await meter.configure(settings); await meter.zero(); await generator.setTiming(profile.timingNumber);
        return [{ key: 'meter', identity: JSON.stringify(meterId), settings: JSON.stringify(readback), calibrationDue: profile.station.calibrationDue },
          { key: 'generator', identity: JSON.stringify(generatorId), settings: JSON.stringify({ timingNumber: profile.timingNumber }), calibrationDue: null },
          { key: 'monitor', identity: JSON.stringify({ ...monitorId, observedInitialSerial: bench.observedInitialSerial }), settings: JSON.stringify({ link: 'RS232', baud: 9600 }), calibrationDue: null }];
      } catch (error) { await bench.close(); throw error; }
    },
    async setRgb(red, green, blue) { await generator.setRgb(red, green, blue); },
    async setBacklight(percent) { return monitor.setBacklight(percent); },
    async measure() { return meter.measureXyY(); },
    async verifySubject(serviceTag) {
      const expected = encodeServiceTag(serviceTag), observed = await monitor.serviceTag();
      if (observed !== expected) throw new ProtocolError('Monitor serial readback differs from the subject service tag.');
      return observed;
    },
    async readEdid() { throw new UnsupportedCapability('No qualified automatic EDID capture is available; independently capture and compare the complete 128-byte EDID.'); },
    async close() {
      const outcomes = await Promise.allSettled(clients.map(client => client.close()));
      bench.cleanupErrors = outcomes.filter(outcome => outcome.status === 'rejected').map(outcome => outcome.reason?.message || 'Serial cleanup did not complete.');
      return { closed: bench.cleanupErrors.length === 0, errors: [...bench.cleanupErrors] };
    },
  };
  return bench;
}
